/** @jsxImportSource @emotion/react */
import { MouseEvent, useState, useEffect, useCallback, BaseSyntheticEvent} from "react";
import { useForm, SubmitHandler, useFieldArray, set } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from "yup";
import { HttpPostData, UploadFile, getCsrfToken} from "../../common/Util";
import { ResponseParamBase } from "../../common/Types";
import { ApiUrl } from "../../common/Params";
import { useDropzone } from "react-dropzone";
import {Alert, AlertColor, Snackbar} from "@mui/material";
import configcss from "../../css/config.module.css";
import pdficon from "../../img/pdficon.png";
import wordicon from "../../img/wordicon.png";
import { CircularProgress, LinearProgress } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import { Snack } from "./Snack";
import { red } from "@mui/material/colors";

export type TokenGaugeData = {
    currentToken: number,
    maxToken: number,
    tokenParse: number,
    tokenProgressColor: string,
}

export type UploadFileProp = {
    filelist: fileListRow[],
    tokenGauge: TokenGaugeData,
}

export type fileListRow = {
    filename: string,
    fileid: number,
    extention: string,
}

type ResponseParam = ResponseParamBase & {
    fileList: fileListRow[],
    tokenGauge: TokenGaugeData,
    /*colorlist: string[],
    backgroundcolor: string,
    bubblebackgroundcolor: string,
    bubbletextcolor: string,
    footerbackgroundcolor: string,
    siteid: string,
    sitename: string,*/
}

type DeleteResponseParam = ResponseParamBase & {
    currentToken: number,
    maxToken: number,
}

export interface IFormInputs {
    fileList: fileListRow[],    
    uploadFile: FileList | null, 
}

const maxFileLength = 260;

const schema = Yup.object({
    uploadFile: Yup.mixed()
    .test('required', 'ファイルが指定されていません', value => {return value && value.length})
    .test('fileSize', `アップロードするファイル名の長さは${maxFileLength}文字までです。`, value => {
        if(value instanceof FileList && value != null) {
            for(let i = 0; i < value.length; i++){
                if(value[i].name.length > maxFileLength){
                    return false;
                }
            }
            return true;
        } else {
            return false;
        }
    })
    .test('extention', 'ファイルの拡張子はdocx又はpdfに限ります。', value => {
        if(value instanceof FileList && value != null) {
            for(let i = 0; i < value.length; i++){
                if(!value[i].name.endsWith('.docx') && !value[i].name.endsWith('.pdf')){
                    return false;
                }
            }
            return true;
        } else {
            return false;
        }
    })
}).required();

export const UploadFileComponent = (prop: UploadFileProp) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const [snackColor, setSnackColor] = useState<AlertColor>("success");
    const [uploadFile, setUploadFile] = useState<FileList | null>();
    const [snackMessage, setSnackMessage] = useState<string>("");
    const [gaugeData, setGaugeData] = useState<TokenGaugeData>(prop.tokenGauge);
    /*const [currentToken, setCurrentToken] = useState<number>(prop.tokenGauge.currentToken);
    const [maxToken, setMaxToken] = useState<number>(prop.tokenGauge.maxToken);
    const [tokenParse, setTokenParse] = useState<number>(prop.totokenParse);
    const [gaugeColor, setGaugeColor] = useState<string>('#52b202');*/

    const { register, handleSubmit, setValue, trigger, control, getValues, watch, formState: { errors, isValid } } = useForm<IFormInputs>({
        defaultValues: {
            fileList: prop.filelist,
            uploadFile: null,
        },
        resolver: yupResolver(schema),
        mode: "onChange",
        reValidateMode: "onChange",
    });

    const {fields, append, remove, replace} = useFieldArray({
        name: "fileList",
        control: control,
    });

    const onDrop = useCallback((files: File[]) => {        
        if(!files){
            
        } else {
            const dt: DataTransfer = new DataTransfer();
            files.map((value, index) => {
                dt.items.add(value);                
            })
            setValue("uploadFile", dt.files);
        }
    }, [])
    const {getRootProps, getInputProps, isDragActive} = useDropzone({
        onDrop,
        accept: {
            'docx/pdf': ['.docx', '.pdf'],
        },
        multiple: true,
    }) 

    const onSubmit: SubmitHandler<IFormInputs> = async (data: IFormInputs, event?: BaseSyntheticEvent) => {
        await UploadFile<ResponseParam>(ApiUrl.UploadFile, 
        data, setIsLoading)
        .then(async response => {
            setSnackColor((value) => {return "success"});
            setSnackMessage((value) => { return response.data.message });
            setOpen((value) => { return true });
            await UploadFile<ResponseParam>(ApiUrl.UserFileList, {}, setIsLoading)
            .then((response) => {
                if(response.data.resultFlg) {
                    replace(response.data.fileList);
                    setValue("uploadFile", null);
                    setGaugeData((value) => {return response.data.tokenGauge});
                } else {
                    setSnackColor((value) => {return "warning"});
                    setSnackMessage((value) => { return response.data.message });
                    setOpen((value) => { return true });
                }
            });
        })
        .catch((e) => {
            setSnackColor((value) => {return "warning"});
            setSnackMessage((value) => { return "ファイルのアップロード時にエラーが発生しました。" });
            setOpen((value) => { return true });
        });
    }

    async function init() {
        await UploadFile<ResponseParam>(ApiUrl.UserFileList, {}, setIsLoading)
            .then((response) => {
                replace(response.data.fileList);
                setValue("uploadFile", null);
                setGaugeData((value) => {return response.data.tokenGauge});
                /*setMaxToken((value) => { return response.data.maxToken });
                setCurrentToken((value) => { return response.data.currentToken });
                setTokenParse((value) => { return response.data.tokenParse });
                setGaugeColor((value) => { return setGrafhColor(response.data.tokenParse) });*/
            });
    }

    const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') {
          return;
        }
        setOpen(false);
    };

    async function downloadData(e: MouseEvent<HTMLImageElement, globalThis.MouseEvent>, fileid: number, filename: string) {
        await UploadFile<Blob>(
            ApiUrl.DownloadDocumentFile,
            {
                'fileid': fileid
            },
            setIsLoading,
            "blob"
        ).then((response) => {
            const link = document.createElement("a");
            link.href = URL.createObjectURL(response.data);
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        });
    }

    function getFileIcon(extention: string) : string {
        switch (extention) {
            case ".pdf":
                return pdficon;                
            case ".docx":
                return wordicon;
            default:
                return pdficon;
        }
    }

    const numberFormat = (num: number): string => {
        return new Intl.NumberFormat().format(num);
      };
      

    useEffect(() => {        
    }, [])

    return(
        <>        
        <div className={`${configcss['infoBox']} ${configcss['config-header']}`}>
        <h2>アップロード</h2>
        <div className={`${configcss['infoBoxInner']} ${configcss['mdlBox']}`}>
            <div style={{display: "flex", flexDirection: "row", justifyContent: "space-around", alignItems: "center"}}>
                <Gauge
                    width={100}
                    height={100}
                    value={gaugeData.tokenParse}
                    sx={(theme) => ({    
                        [`& .${gaugeClasses.valueText}`]: {
                            fontSize: 30,
                        },                
                        [`& .${gaugeClasses.valueArc}`]: {
                        fill: gaugeData.tokenProgressColor,
                        },
                        [`& .${gaugeClasses.referenceArc}`]: {
                        fill: "#FFFFFF",
                        },
                    })}
                />                
                    資料サイズ（トークン）：{numberFormat(gaugeData.currentToken)}／{numberFormat(gaugeData.maxToken)}                
            </div>
            <form onSubmit={(e) => { handleSubmit(onSubmit)(e) }}>
            {
                fields.map((field, index) => {
                    return (
                        <div key={field.fileid} className={`${configcss['infoBoxInner-sub']} ${configcss['layoutBox']}`} >
                            <img className={configcss['fileImage']} src={getFileIcon(field.extention)} onClick={(e) => {downloadData(e, field.fileid, field.filename)}}></img>
                            <div className={configcss['fileName']}>{field.filename}</div>                            
                            <input type="button" value={"削除"} className={configcss['infoBoxBtn']}
                                onClick={async (e) => {
                                    await UploadFile<DeleteResponseParam>(ApiUrl.DeleteFile, {'deletefile': field.filename}, setIsLoading)
                                    .then(async (response) => {
                                        await init();
                                    }).catch((e) => {
                                        setSnackColor((value) => {return "warning"});
                                        setSnackMessage((value) => {return "ファイルの削除に失敗しました。"});
                                        setOpen(true);
                                    })
                                }}
                            ></input>
                        </div>
                    )
                })
            }
                <div {...getRootProps()} className={`${configcss['infoBoxInner-sub']} ${configcss['layoutBox']}`}>
                    <div className={configcss['infoBoxFlex']}>
                        <div className={configcss['infoBoxFile']}>
                            <input {...getInputProps()}/>
                            ファイルを選択またはドラッグアンドドロップ
                        </div>
                        <div className={configcss['error']}>{errors.uploadFile?.message}</div>
                    </div>
                </div>
            {
                watch("uploadFile")?.length ?? 0 > 0 ?
                <div>{watch("uploadFile")?.length}件のファイルを選択中...</div>
                :
                null
            }
            {
                isLoading ?
                <CircularProgress></CircularProgress>
                :
                <input type="submit" disabled={isLoading || gaugeData.tokenParse >= 100} className={`${configcss['infoBoxBtn']}`}></input>
            }            
            </form>            
            </div>
        </div>
        <Snack open={open} setOpen={setOpen} color={snackColor} message={snackMessage} duration={6000} anchor={{horizontal: "left", vertical: "top"}}></Snack>
        </>
    );
}